import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query'
import { RouterProvider } from 'react-router-dom'
import { router } from './routes/router'
import { ToastProvider } from '@/components/common'
import { emitGlobalError } from '@/hooks/useGlobalErrorHandler'
import { GlobalErrorHandler } from './components/GlobalErrorHandler'
import { useAuthStore } from '@/stores/useAuthStore'
import { fetchAuthMe, setAuthToken } from '@/api/auth'
import './index.css'

/**
 * QueryCache with global error handling
 * Emits errors to the global error emitter for toast notifications
 */
const queryCache = new QueryCache({
  onError: (error, query) => {
    // Only emit if the query doesn't have its own error handling
    // Check for meta.skipGlobalErrorHandler to allow opt-out
    if (query.meta?.skipGlobalErrorHandler) {
      return
    }
    emitGlobalError(error)
  },
})

/**
 * MutationCache with global error handling
 * Emits errors to the global error emitter for toast notifications
 */
const mutationCache = new MutationCache({
  onError: (error, _variables, _context, mutation) => {
    // Only emit if the mutation doesn't have its own error handling
    // Check for meta.skipGlobalErrorHandler to allow opt-out
    if (mutation.meta?.skipGlobalErrorHandler) {
      return
    }
    emitGlobalError(error)
  },
})

/**
 * QueryClient with global caches and default options
 */
const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 2,
    },
  },
})

/**
 * Bootstrap the app:
 * 1. Try localStorage (fast, synchronous)
 * 2. If no token, try cookie exchange via GET /auth/me
 * 3. Render — ProtectedRoute will redirect to /login if still unauthenticated
 */
const rootEl = document.getElementById('root')!;
rootEl.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:system-ui;color:#6b7280"><p>Loading...</p></div>';

async function bootstrap() {
  const store = useAuthStore.getState();
  store.hydrate();

  if (!useAuthStore.getState().isAuthenticated) {
    try {
      const { token } = await fetchAuthMe();
      setAuthToken(token);
      store.hydrate();

      // After successful cookie exchange, restore the saved return URL
      // (saved by ProtectedRoute before redirecting to /login)
      const RETURN_URL_KEY = 'apx_insight_return_url';
      const returnUrl = sessionStorage.getItem(RETURN_URL_KEY);
      if (returnUrl) {
        sessionStorage.removeItem(RETURN_URL_KEY);
        window.history.replaceState(null, '', returnUrl);
      }
    } catch (err) {
      // Surface non-401 errors so LoginPage can display them
      if (err instanceof Error && !err.message.includes('Not authenticated')) {
        sessionStorage.setItem('apx_auth_error', err.message);
      }
    }
  }

  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <GlobalErrorHandler />
          <RouterProvider router={router} />
        </ToastProvider>
      </QueryClientProvider>
    </StrictMode>,
  )
}

bootstrap();
